/**
 * Chart primitives, hand-drawn in SVG.
 *
 * No charting library: this app carries react and react-dom and nothing else,
 * and recharts would bring a d3 subtree along for six shapes. More to the
 * point, DESIGN.md asks for a ledger rather than a console — hairline rules,
 * tabular numerals, one restrained accent — and a stock chart library fights
 * that on every default it ships with.
 *
 * Two rules everything here follows:
 *
 *   1. Charts are MEASURED, not scaled. Each draws at the container's real
 *      pixel width, so a hairline is one device pixel and a 12px label is 12px
 *      on a phone as well as a laptop. A fixed viewBox stretched by CSS would
 *      turn both into whatever the scale factor happened to be.
 *   2. No data draws NOTHING. Every primitive renders a quiet line of prose
 *      instead of an empty frame, because an axis with no series on it reads as
 *      "zero" when the truth is "we do not collect this yet".
 */

import { useCallback, useRef, useState, type ReactNode } from 'react';

export interface Point {
  label: string;
  value: number;
}

export interface Series {
  name: string;
  points: Point[];
  /** Index into the series ramp; defaults to declaration order. */
  tone?: number;
}

/**
 * The series ramp — an extension to DESIGN.md's palette, added deliberately.
 *
 * The base system has ONE accent, plus danger and warn which are reserved for
 * meaning and must never be spent on "this is the second line". Comparing two
 * brands needs a second and third hue that carry no meaning at all. These sit
 * in a narrow lightness band, so no series looks more important than another.
 *
 * Tuned for the LIGHT teal ground (2026-10-21): the ramp starts with the teal
 * accent, and every tone sits near L 0.48–0.62 so a line holds at least 3:1
 * against a white panel. Hue does most of the separating, lightness helps
 * neighbours apart, and all stay clear of danger (hue 25) and warn (hue 70).
 */
const SERIES_TONES = [
  'oklch(0.62 0.12 190)',
  'oklch(0.52 0.15 262)',
  'oklch(0.58 0.17 330)',
  'oklch(0.6 0.14 140)',
  'oklch(0.5 0.13 295)',
  'oklch(0.62 0.11 235)',
  'oklch(0.48 0.07 200)',
  'oklch(0.6 0.12 110)',
];

/** Axis text and grid hairlines, read from the theme tokens in styles.css. */
export const CHART_AXIS = 'var(--ink-faint)';
export const CHART_GRID = 'var(--rule)';

export function seriesTone(index: number): string {
  // A report theme can set --series-0 … --series-7; otherwise the house tones.
  const slot = index % SERIES_TONES.length;
  return `var(--series-${slot}, ${SERIES_TONES[slot] ?? SERIES_TONES[0]!})`;
}

/**
 * Container width in real pixels, via a CALLBACK ref.
 *
 * Not useRef + useEffect([]). Every chart here renders a "no data yet" line
 * INSTEAD of its container while data is loading, so on a page that fetches
 * asynchronously the first mount has no element to measure — and an effect
 * with empty deps never runs again once the element does appear. The charts
 * stayed blank forever in production while rendering perfectly in a harness
 * that had its data up front. A callback ref fires whenever the node attaches
 * or detaches, which is exactly the event we need.
 */
function useMeasuredWidth(): [(node: HTMLDivElement | null) => void, number] {
  const [width, setWidth] = useState(0);
  const detach = useRef<(() => void) | null>(null);

  const ref = useCallback((node: HTMLDivElement | null) => {
    detach.current?.();
    detach.current = null;
    if (!node) return;

    // Deferred to the next frame, and ignored unless it actually moved.
    // Writing a width synchronously from inside the observer re-lays-out the
    // very element being observed; with an SVG carrying an explicit width that
    // oscillates between two values and locks the renderer solid. (It did.)
    let frame = 0;
    const observer = new ResizeObserver((entries) => {
      const measured = Math.max(0, Math.floor(entries[0]?.contentRect.width ?? 0));
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        setWidth((current) => (Math.abs(current - measured) >= 1 ? measured : current));
      });
    });
    observer.observe(node);
    setWidth(Math.max(0, Math.floor(node.getBoundingClientRect().width)));
    detach.current = () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  return [ref, width];
}

function Empty({ note }: { note: string }): ReactNode {
  return <p className="chart-empty">{note}</p>;
}

function ChartFrame({
  children,
  height,
  width,
  containerRef,
  label,
}: {
  children: (width: number) => ReactNode;
  height: number;
  width: number;
  containerRef: (node: HTMLDivElement | null) => void;
  label: string;
}): ReactNode {
  return (
    <div className="chart" ref={containerRef}>
      {width > 0 ? (
        <svg width={width} height={height} role="img" aria-label={label}>
          {children(width)}
        </svg>
      ) : null}
    </div>
  );
}

/** Nice-ish upper bound so the axis reads 0 / 50 / 100 rather than 0 / 47 / 94. */
function niceCeiling(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalised = value / magnitude;
  const step = normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10;
  return step * magnitude;
}

const PAD_LEFT = 44;
const PAD_RIGHT = 8;
const PAD_TOP = 10;
const PAD_BOTTOM = 22;

/**
 * A time series. Optional `band` draws a shaded range behind the first series —
 * that is what a forecast's lower and upper bounds are, and drawing them as a
 * band rather than two more lines keeps "this is uncertainty" visually distinct
 * from "this is another measure".
 */
export function LineChart({
  series,
  height = 200,
  formatValue = (value: number) => String(value),
  band,
  emptyNote = 'Nothing recorded for this period yet.',
  area = false,
}: {
  series: Series[];
  height?: number;
  formatValue?: (value: number) => string;
  band?: { lower: number[]; upper: number[] } | undefined;
  emptyNote?: string;
  /** Fill under each line (the Area visualisation). */
  area?: boolean;
}): ReactNode {
  const [ref, width] = useMeasuredWidth();
  const live = series.filter((entry) => entry.points.length > 0);
  if (live.length === 0) return <Empty note={emptyNote} />;

  const length = Math.max(...live.map((entry) => entry.points.length));
  const highest = Math.max(
    ...live.flatMap((entry) => entry.points.map((point) => point.value)),
    ...(band?.upper ?? []),
  );
  const top = niceCeiling(highest);

  const plotWidth = Math.max(1, width - PAD_LEFT - PAD_RIGHT);
  const plotHeight = Math.max(1, height - PAD_TOP - PAD_BOTTOM);
  const xAt = (index: number) =>
    PAD_LEFT + (length <= 1 ? plotWidth / 2 : (index / (length - 1)) * plotWidth);
  const yAt = (value: number) => PAD_TOP + plotHeight - (value / top) * plotHeight;

  const gridValues = [0, top / 2, top];
  const labels = live[0]?.points ?? [];
  // At narrow widths every third tick keeps the axis readable instead of a smear.
  const tickEvery = Math.max(1, Math.ceil(length / Math.max(2, Math.floor(plotWidth / 64))));

  return (
    <ChartFrame containerRef={ref} width={width} height={height} label={live[0]?.name ?? 'chart'}>
      {() => (
        <>
          {gridValues.map((value) => (
            <g key={value}>
              <line
                x1={PAD_LEFT}
                x2={width - PAD_RIGHT}
                y1={yAt(value)}
                y2={yAt(value)}
                className="chart-grid"
              />
              <text x={0} y={yAt(value) + 4} className="chart-axis">
                {formatValue(value)}
              </text>
            </g>
          ))}

          {band && band.lower.length === length ? (
            <path
              className="chart-band"
              d={`${band.upper.map((value, index) => `${index === 0 ? 'M' : 'L'}${xAt(index)},${yAt(value)}`).join(' ')} ${band.lower
                .map((value, index, all) => {
                  const position = all.length - 1 - index;
                  return `L${xAt(position)},${yAt(all[position] ?? 0)}`;
                })
                .join(' ')} Z`}
            />
          ) : null}

          {live.map((entry, entryIndex) => {
            const tone = seriesTone(entry.tone ?? entryIndex);
            const path = entry.points
              .map((point, index) => `${index === 0 ? 'M' : 'L'}${xAt(index)},${yAt(point.value)}`)
              .join(' ');
            const last = entry.points[entry.points.length - 1];
            return (
              <g key={entry.name}>
                {area && entry.points.length > 1 && (
                  <path
                    d={`${path} L${xAt(entry.points.length - 1)},${yAt(0)} L${xAt(0)},${yAt(0)} Z`}
                    className="chart-area"
                    style={{ fill: tone }}
                  />
                )}
                <path d={path} className="chart-line" style={{ stroke: tone }} />
                {last ? (
                  <circle
                    cx={xAt(entry.points.length - 1)}
                    cy={yAt(last.value)}
                    r={3}
                    style={{ fill: tone }}
                  />
                ) : null}
              </g>
            );
          })}

          {labels.map((point, index) =>
            index % tickEvery === 0 || index === length - 1 ? (
              <text
                key={point.label}
                x={xAt(index)}
                y={height - 6}
                className="chart-axis chart-tick"
              >
                {point.label}
              </text>
            ) : null,
          )}
        </>
      )}
    </ChartFrame>
  );
}

/** Vertical bars — counts over time, where a line would imply a continuous quantity. */
export function BarChart({
  points,
  height = 180,
  formatValue = (value: number) => String(value),
  toneFor,
  emptyNote = 'Nothing recorded for this period yet.',
}: {
  points: Point[];
  height?: number;
  formatValue?: (value: number) => string;
  toneFor?: (point: Point, index: number) => string;
  emptyNote?: string;
}): ReactNode {
  const [ref, width] = useMeasuredWidth();
  if (points.length === 0) return <Empty note={emptyNote} />;

  const top = niceCeiling(Math.max(...points.map((point) => point.value)));
  const plotWidth = Math.max(1, width - PAD_LEFT - PAD_RIGHT);
  const plotHeight = Math.max(1, height - PAD_TOP - PAD_BOTTOM);
  const slot = plotWidth / points.length;
  const barWidth = Math.max(2, Math.min(28, slot * 0.6));
  const tickEvery = Math.max(1, Math.ceil(points.length / Math.max(2, Math.floor(plotWidth / 64))));

  return (
    <ChartFrame containerRef={ref} width={width} height={height} label="bar chart">
      {() => (
        <>
          {[0, top / 2, top].map((value) => (
            <g key={value}>
              <line
                x1={PAD_LEFT}
                x2={width - PAD_RIGHT}
                y1={PAD_TOP + plotHeight - (value / top) * plotHeight}
                y2={PAD_TOP + plotHeight - (value / top) * plotHeight}
                className="chart-grid"
              />
              <text
                x={0}
                y={PAD_TOP + plotHeight - (value / top) * plotHeight + 4}
                className="chart-axis"
              >
                {formatValue(value)}
              </text>
            </g>
          ))}
          {points.map((point, index) => {
            const barHeight = (point.value / top) * plotHeight;
            return (
              <rect
                key={`${point.label}-${index}`}
                x={PAD_LEFT + slot * index + (slot - barWidth) / 2}
                y={PAD_TOP + plotHeight - barHeight}
                width={barWidth}
                height={Math.max(point.value > 0 ? 1 : 0, barHeight)}
                className="chart-bar"
                style={toneFor ? { fill: toneFor(point, index) } : undefined}
              >
                <title>{`${point.label}: ${formatValue(point.value)}`}</title>
              </rect>
            );
          })}
          {points.map((point, index) =>
            index % tickEvery === 0 || index === points.length - 1 ? (
              <text
                key={`tick-${point.label}-${index}`}
                x={PAD_LEFT + slot * index + slot / 2}
                y={height - 6}
                className="chart-axis chart-tick"
              >
                {point.label}
              </text>
            ) : null,
          )}
        </>
      )}
    </ChartFrame>
  );
}

/**
 * One horizontal bar split into states — open / in review / done.
 *
 * A pie would need a legend to be read at all; a single bar with the counts
 * written on it reads at a glance and survives being 340px wide.
 */
export function SegmentBar({
  segments,
  emptyNote = 'No tasks yet.',
}: {
  segments: { label: string; value: number; tone: string }[];
  emptyNote?: string;
}): ReactNode {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);
  if (total === 0) return <Empty note={emptyNote} />;
  return (
    <div className="segment">
      <div className="segment-bar">
        {segments
          .filter((segment) => segment.value > 0)
          .map((segment) => (
            <span
              key={segment.label}
              className="segment-fill"
              style={{ width: `${(segment.value / total) * 100}%`, background: segment.tone }}
              title={`${segment.label}: ${segment.value}`}
            />
          ))}
      </div>
      <ul className="segment-key">
        {segments.map((segment) => (
          <li key={segment.label}>
            <span className="segment-dot" style={{ background: segment.tone }} aria-hidden="true" />
            <span className="segment-label">{segment.label}</span>
            <span className="mono segment-value">{segment.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A 0-100 score as a measured bar. Used for trust dimensions and hit rates. */
export function ScoreBars({
  rows,
  emptyNote = 'Not scored yet.',
}: {
  rows: { label: string; score: number | null; note?: string }[];
  emptyNote?: string;
}): ReactNode {
  const scored = rows.filter((row) => row.score !== null);
  if (scored.length === 0) return <Empty note={emptyNote} />;
  return (
    <ul className="scorebars">
      {rows.map((row) => (
        <li key={row.label}>
          <span className="scorebar-label">{row.label}</span>
          <span className="scorebar-track">
            {row.score === null ? null : (
              <span
                className="scorebar-fill"
                style={{
                  width: `${Math.max(0, Math.min(100, row.score))}%`,
                  // Not measured is not the same as measured-and-bad: an
                  // unscored dimension stays empty rather than reading zero.
                  background:
                    row.score >= 80
                      ? 'var(--accent)'
                      : row.score >= 60
                        ? 'var(--warn)'
                        : 'var(--danger)',
                }}
              />
            )}
          </span>
          <span className="mono scorebar-value">
            {row.score === null ? 'not measured' : Math.round(row.score)}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** An inline trend, no axes — for a figure that already states its own value. */
export function Sparkline({
  values,
  width = 96,
  height = 24,
}: {
  values: number[];
  width?: number;
  height?: number;
}): ReactNode {
  if (values.length < 2) return null;
  const highest = Math.max(...values);
  const lowest = Math.min(...values);
  const span = highest - lowest || 1;
  const xAt = (index: number) => (index / (values.length - 1)) * (width - 4) + 2;
  const yAt = (value: number) => height - 3 - ((value - lowest) / span) * (height - 6);
  const path = values
    .map((value, index) => `${index === 0 ? 'M' : 'L'}${xAt(index)},${yAt(value)}`)
    .join(' ');
  const last = values[values.length - 1] ?? 0;
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="sparkline"
      aria-hidden="true"
    >
      <path d={path} className="chart-line" />
      <circle cx={xAt(values.length - 1)} cy={yAt(last)} r={2.5} className="sparkline-end" />
    </svg>
  );
}
